import { ActivatedRoute, RouterModule } from '@angular/router';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';

import { APP_CONFIG } from '@kros-sk/core/app-config';
import { AuthDispatchersService, PermissionsService } from '@kros-sk/auth';
import { NavigationService } from '@kros-sk/ui/navigation';
import { PAGE_NOT_FOUND_QUERY_KEY, PageNotFoundParam, PageNotFoundVisualDataSource } from '@kros-sk/models';

@Component({
  selector: 'kros-page-not-found',
  templateUrl: './page-not-found.component.html',
  styleUrls: ['./page-not-found.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule],
})
export class PageNotFoundComponent implements OnDestroy, OnInit {
  protected readonly route = inject(ActivatedRoute);
  protected readonly navService = inject(NavigationService);
  protected readonly authDispatcher = inject(AuthDispatchersService);
  protected readonly permissionsService = inject(PermissionsService);
  protected readonly appConfig = inject(APP_CONFIG);

  protected visualDataSource = this.generateVisualDataSource();
  protected idForRedirect = +this.route.snapshot.queryParamMap.get('idForRedirect');

  #pageType = signal<keyof typeof PageNotFoundParam>(PageNotFoundParam.pageNotFound);
  protected pageType = this.#pageType.asReadonly();

  protected shouldRedirectToCompanyRoute = computed(() => {
    const pageType = this.pageType();

    return (
      pageType === PageNotFoundParam[PageNotFoundParam.licenseUserCountExceeded] ||
      pageType === PageNotFoundParam[PageNotFoundParam.licenseCompaniesCountExceeded] ||
      pageType === PageNotFoundParam[PageNotFoundParam.noPermission]
    );
  });

  protected routerLinkParams = computed(() => {
    if (this.shouldRedirectToCompanyRoute()) {
      if (this.idForRedirect) {
        return ['/company', this.idForRedirect];
      } else {
        return ['/company', 'create'];
      }
    } else {
      return ['/'];
    }
  });

  ngOnInit(): void {
    this.navService.hide(true);
    const queryParam = this.route.snapshot.queryParamMap.get(PAGE_NOT_FOUND_QUERY_KEY) as PageNotFoundParam;
    this.#pageType.set(PageNotFoundParam[queryParam || PageNotFoundParam.pageNotFound]);
  }

  ngOnDestroy(): void {
    this.navService.show();
  }

  protected logOut(): void {
    this.authDispatcher.logoutUser();
  }

  protected clearLoadedPermissions(): void {
    this.permissionsService.clearLoadedPermissions();
  }

  private createImgUrl(imgFileName: string): string {
    const staticUrl = this.appConfig.staticContentStorage;
    return `${staticUrl}/empty-states/${imgFileName}`;
  }

  private generateVisualDataSource(): PageNotFoundVisualDataSource {
    return {
      pageNotFound: {
        textLines: ['Stránka sa nenašla!'],
        imgSrc: this.createImgUrl('error-404.svg'),
      },
      companyNotFound: {
        textLines: ['Firma nie je v zozname dostupných firiem', 'alebo do aplikácie nemáte povolený prístup.'],
        imgSrc: this.createImgUrl('limited-access.svg'),
      },
      licenseNotLoaded: {
        textLines: ['Nepodarilo sa overiť licenciu!'],
        imgSrc: this.createImgUrl('limited-access.svg'),
      },
      noPermission: {
        textLines: ['Nemáte dostatočné prístupové práva!'],
        imgSrc: this.createImgUrl('error-403.svg'),
      },
      companyDeleted: {
        textLines: ['Firma bola vymazaná!'],
        imgSrc: this.createImgUrl('deleted.svg'),
      },
      userDeletedFromCompany: {
        textLines: ['Do tejto firmy nemáte prístupové práva!'],
        imgSrc: this.createImgUrl('deleted.svg'),
      },
      licenseUserCountExceeded: {
        heading: 'Do tejto firmy nemáte prístup.',
        textLines: [
          'Počet používateľov v licencii bol zmenený.',
          `Kontaktujte správcu ${this.route.snapshot.queryParamMap.get('email')}, ` +
            'aby nastavil prístupy zodpovedajúce počtu používateľov v licencii.',
        ],
        imgSrc: this.createImgUrl('license-exceeded.svg'),
        actionLogout: true,
      },
      licenseCompaniesCountExceeded: {
        heading: 'Do tejto firmy nemáte prístup.',
        textLines: [
          'Počet firiem v licencii bol zmenený.',
          `Kontaktujte správcu ${this.route.snapshot.queryParamMap.get('email')}, ` +
            'aby nastavil prístupy zodpovedajúce počtu firiem v licencii.',
        ],
        imgSrc: this.createImgUrl('license-exceeded.svg'),
        actionLogout: true,
      },
    };
  }
}
